import { useCallback, useState } from 'react';
import { isImageHandle, thumbnailFor, useChatImageScope, type ChatImageScope } from './chat-image-scope';
import { MediaThumbnail, type MediaThumbnailState } from './MediaThumbnail';

export interface MessageImagePlaceholder {
  /** The `image://{id}` handle from the attachment entry's `attachment_path`. */
  handle: string;
  /**
   * The attachment entry's stored stub text (the raw copy sent to the LLM).
   * Kept in the shape for transcript plumbing but never rendered — the bubble
   * shows the image thumbnail itself.
   */
  content?: string;
}

export interface MessageImagePlaceholdersProps {
  placeholders: MessageImagePlaceholder[];
}

/**
 * Sent-message image thumbnails, rendered from the persisted `attachment`
 * context entries — so they survive a reload and a second reader sees them.
 * Bytes live only in Redis with a short TTL (sessions ADR-9166f); an expired
 * image renders MediaThumbnail's expired state.
 */
export function MessageImagePlaceholders({ placeholders }: MessageImagePlaceholdersProps) {
  if (placeholders.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="message-images">
      {placeholders.map((placeholder) => (
        <MessageImageThumbnail key={placeholder.handle} handle={placeholder.handle} />
      ))}
    </div>
  );
}

function MessageImageThumbnail({ handle }: { handle: string }) {
  if (!isImageHandle(handle)) {
    throw new Error(`message image placeholder carries a non-image handle: ${handle}`);
  }
  const scope = useChatImageScope();
  const [loaded, setLoaded] = useState<{ scope: ChatImageScope; state: MediaThumbnailState } | null>(null);
  const state: MediaThumbnailState = loaded?.scope === scope ? loaded.state : { kind: 'idle' };
  const load = useCallback(() => {
    setLoaded({ scope, state: { kind: 'loading' } });
    void thumbnailFor(scope, handle).then((next) => setLoaded({ scope, state: next }));
  }, [scope, handle]);
  return (
    <div data-image-handle={handle}>
      <MediaThumbnail
        state={state}
        label="Attached image"
        testId="message-image-placeholder"
        lightboxTestId="message-image-lightbox"
        onVisible={load}
        onRetry={load}
      />
    </div>
  );
}
